/**
 * Streaming client (P1-T05): fetches manifest, loads cells per the pure
 * policy in streamingPolicy.ts using Babylon SceneLoader with worker-side
 * glTF parsing (SPEC-02 §3.7: no >4 ms main-thread work). LOD1 -> LOD0 swap
 * uses a screen-door dither cross-fade over 0.3 s (§3.4).
 *
 * Exposes __KN.streaming (lruMB hook, stats) from main.ts.
 */
import type { Scene } from 'babylonjs';
import {
  SceneLoader,
  ShaderMaterial,
  Color3,
  Texture,
  Mesh,
  Matrix,
  Vector3,
  AbstractEngine,
} from 'babylonjs';
import type { Nullable } from 'babylonjs';
import type { Tier } from '@kota-nova/shared';
import { CellLru, desiredSet, loadPriority, PLAN, type DesiredCell, type Lod } from './streamingPolicy';
import { configureAssetLoader } from './loaderSetup';

export interface ManifestFileEntry {
  url: string;
  bytes: number;
  sha256: string;
  triangles?: number;
  materials?: number;
}
export interface ManifestCell {
  district: string;
  lod0: ManifestFileEntry;
  lod1: ManifestFileEntry;
  instances?: ManifestFileEntry;
  colliders?: ManifestFileEntry;
  bounds?: { minY: number; maxY: number };
}
export interface Manifest {
  version: 1;
  build: string;
  worldSeed: number;
  cellSize: number;
  origin: [number, number, number];
  cells: Record<string, ManifestCell>;
  shared: Record<string, ManifestFileEntry>;
  skyline?: Record<string, ManifestFileEntry>;
}

export interface StreamingOptions {
  scene: Scene;
  babylon: typeof import('babylonjs');
  tier: Tier;
  assetBase: string; // e.g. https://assets.sintim.io
  sliceBounds: { min: number; max: number };
  cameraPos: () => { x: number; z: number; altitude: number; fx: number; fz: number };
  onInstances?: (cell: string, instances: InstanceList) => void;
  /** Building/fountain boxes in the same client space as instance matrices. */
  onColliders?: (
    cell: string,
    boxes: { x0: number; x1: number; z0: number; z1: number; y0: number; y1: number }[],
  ) => void;
  /** Fired when a cell's LOD0 unloads so prop/collider owners can clean up. */
  onCellUnloaded?: (cell: string) => void;
  /** Called once per freshly loaded container (P1-T06: material upgrade + dedupe). */
  onCellLoaded?: (container: import('babylonjs').AssetContainer) => void;
}

export interface InstanceList {
  instances: { type: string; m: number[] }[];
}

interface CellHandle {
  root: Nullable<Mesh>; // LOD0
  proxy: Nullable<Mesh>; // LOD1
  rootReady: boolean;
  fadeStartMs: number; // cross-fade in progress when > 0
  loadedAtS: Map<Lod, number>;
}

export class StreamingClient {
  manifest: Nullable<Manifest> = null;
  private readonly lru = new CellLru<void>();
  private readonly cells = new Map<string, CellHandle>();
  private readonly wanted = new Map<string, Lod>();
  private readonly loading = new Set<string>();
  /** Keys the LRU evicted at insert time; plan() disposes them next tick. */
  private readonly pendingEvict = new Set<string>();
  private tickAccum = 0;
  private elapsedS = 0;
  readonly stats = { loaded: 0, loadingBytes: 0, downloads: 0, ktx2: true, meshopt: true };

  constructor(private readonly opts: StreamingOptions) {}

  get lruMB(): number {
    return this.lru.budgetMB;
  }
  set lruMB(v: number) {
    this.lru.budgetMB = v;
    for (const key of this.lru.evict()) this.unloadKey(key);
  }

  async fetchManifest(): Promise<Manifest> {
    const r = await fetch(`${this.opts.assetBase}/manifest.json`, { cache: 'no-cache' });
    if (!r.ok) throw new Error(`manifest HTTP ${String(r.status)}`);
    const m = (await r.json()) as Manifest;
    this.manifest = m;
    return m;
  }

  /** Register loaders/decoders (idempotent) — vendored, no CDN at runtime. */
  async configureLoader(_engine: AbstractEngine): Promise<void> {
    configureAssetLoader();
  }

  /** Call from the render loop with frame delta (seconds). */
  tick(dtS: number): void {
    this.tickAccum += dtS;
    this.elapsedS += dtS;
    this.stepFades();
    if (this.tickAccum < PLAN.tickS) return;
    this.tickAccum = 0;
    this.plan();
  }

  private plan(): void {
    if (!this.manifest) return;
    const cam = this.opts.cameraPos();
    const ids = new Set(Object.keys(this.manifest.cells));
    const want = desiredSet(this.opts.tier, cam.x, cam.z, cam.altitude, ids, this.opts.sliceBounds);
    this.wanted.clear();
    for (const d of want) this.wanted.set(d.cell, d.lod);
    // ensure handles
    for (const d of want)
      if (!this.cells.has(d.cell))
        this.cells.set(d.cell, {
          root: null,
          proxy: null,
          rootReady: false,
          fadeStartMs: 0,
          loadedAtS: new Map(),
        });
    // queue at most PLAN.parallel loads
    const loadedLods = new Map<string, Lod>();
    for (const [id, h] of this.cells) {
      if (h.root && h.rootReady) loadedLods.set(id, 0);
      else if (h.proxy) loadedLods.set(id, 1);
    }
    const prio = loadPriority(want, loadedLods, cam.x, cam.z, cam.fx, cam.fz);
    let inflight = this.loading.size;
    for (const d of prio) {
      if (inflight >= PLAN.parallel) break;
      const key = `${d.cell}:${String(d.lod)}`;
      if (this.loading.has(key)) continue;
      void this.loadCell(d);
      inflight++;
    }
    // LRU budget evictions from inserts since last tick (was `void evicted`:
    // bookkeeping dropped the keys but the meshes stayed in the scene — the
    // 30-min soak leak)
    if (this.pendingEvict.size > 0) {
      for (const key of this.pendingEvict) {
        const [id, lod] = key.split(':');
        if (id && lod && !this.loading.has(key)) this.unload(id, Number(lod) as Lod);
      }
      this.pendingEvict.clear();
    }
    // unload policy
    for (const [id, h] of this.cells) {
      for (const lod of [0, 1] as const) {
        const at = h.loadedAtS.get(lod);
        if (at === undefined) continue;
        const stale = this.wanted.get(id) !== lod;
        const age = this.elapsedS - at;
        const far = !this.wanted.has(id);
        // Unload anything not wanted after a grace period. (Was: also require
        // ring > 5 of focus — that turned "explore nearby, keep everything"
        // into permanent retention and was the soak growth: a wander within
        // ~4 rings never unloaded a single cell.) Reload is cheap: HTTP cache
        // + worker parse; the 10 s grace prevents boundary flapping.
        if ((stale && lod === 1 && h.rootReady) || (far && age > 10)) {
          this.unload(id, lod);
        }
      }
      // never drop a handle with a load in flight (loadCell re-attaches to it)
      const pending = this.loading.has(`${id}:0`) || this.loading.has(`${id}:1`);
      if (!pending && !h.root && !h.proxy && h.loadedAtS.size === 0) this.cells.delete(id);
    }
  }

  private async loadCell(d: DesiredCell): Promise<void> {
    const m = this.manifest;
    if (!m) return;
    const cell = m.cells[d.cell];
    if (!cell) return;
    const file = d.lod === 0 ? cell.lod0 : cell.lod1;
    const key = `${d.cell}:${String(d.lod)}`;
    const url = `${this.opts.assetBase}/${file.url}`;
    this.loading.add(key);
    try {
      const container = await SceneLoader.LoadAssetContainerAsync(url, undefined, this.opts.scene);
      const h = this.cells.get(d.cell);
      if (!h) {
        container.dispose();
        return;
      }
      container.addAllToScene();
      this.opts.onCellLoaded?.(container);
      const root = new Mesh(`cell_${d.cell}_lod${String(d.lod)}`, this.opts.scene);
      // The Blender Z-up -> glTF (RH Y-up) -> Babylon (LH Y-up) round trip
      // mirrors both X and Z for authored world-space cells. Undo at the cell
      // root so visuals line up with the world-coordinate collider boxes.
      root.scaling.x = -1;
      root.scaling.z = -1;
      // The glTF loader parents everything under an unnamed `__root__` node
      // which lands in container.rootNodes, NOT container.meshes — re-parent
      // root-level nodes (any Node type) so unload() can dispose the whole
      // cell hierarchy; leaving them detached was the 30-min soak leak.
      for (const node of container.rootNodes) {
        if (!node.parent && node !== root) node.parent = root;
      }
      const mb = Math.max(0.05, (file.bytes / 1e6) * 3); // GPU estimate: bytes ~ 1/3 of GPU cost
      // LRU insert; over-budget keys unload on the next plan tick (a key
      // evicted at insert time — lruMB=0 stress — drops on the next tick too)
      for (const evKey of this.lru.put(key, undefined, mb)) this.pendingEvict.add(evKey);
      if (d.lod === 0) {
        h.root = root;
        h.rootReady = true;
        h.fadeStartMs = performance.now();
        if (h.proxy) {
          /* proxy removed at fade end */
        }
      } else if (h.rootReady && h.fadeStartMs === 0) {
        // race: the LOD1 request was queued while LOD0 was far; by the time
        // this slow parse landed LOD0 is fully faded in — the proxy has no
        // fade left to dispose it, so drop it now (was the soak orphan leak)
        root.dispose(false, false);
        this.lru.drop(`${d.cell}:1`);
      } else {
        h.proxy = root;
      }
      h.loadedAtS.set(d.lod, this.elapsedS);
      this.stats.downloads++;
      this.stats.loaded = this.cells.size;
      // instances + colliders (once per cell)
      if (d.lod === 0 && cell.instances && this.opts.onInstances) {
        try {
          const ir = await fetch(`${this.opts.assetBase}/${cell.instances.url}`);
          if (ir.ok) this.opts.onInstances(d.cell, (await ir.json()) as InstanceList);
        } catch {
          /* instance lists are best-effort */
        }
      }
      if (d.lod === 0 && cell.colliders && this.opts.onColliders) {
        try {
          const cr = await fetch(`${this.opts.assetBase}/${cell.colliders.url}`);
          if (cr.ok) {
            const j = (await cr.json()) as {
              boxes: { x0: number; x1: number; z0: number; z1: number; y0: number; y1: number }[];
            };
            this.opts.onColliders(d.cell, j.boxes);
          }
        } catch {
          /* colliders are best-effort; visual world still works */
        }
      }
      void url;
    } finally {
      this.loading.delete(key);
    }
  }

  /** P1-T13: art-test freezes the clock for still-frame pairs; LOD fades run
   *  on wall-clock time, so they must pause too (a mid-pair fade reads as
   *  z-fighting flicker). */
  clockFrozen = false;

  /** Screen-door dither: fade LOD0 in over PLAN.fadeS via visibility. */
  private stepFades(): void {
    if (this.clockFrozen) return;
    const now = performance.now();
    for (const [, h] of this.cells) {
      if (h.fadeStartMs > 0) {
        const t = (now - h.fadeStartMs) / (PLAN.fadeS * 1000);
        if (t >= 1) {
          if (h.root) h.root.visibility = 1;
          h.fadeStartMs = 0;
          if (h.proxy) {
            h.proxy.dispose(false, true);
            h.proxy = null;
            this.lru.drop(`${cellIdOf(h)}:1`);
            h.loadedAtS.delete(1);
          }
        } else {
          // dither ramp: cheap 2-way crossfade via visibility (screen-door
          // shader upgrade lands with P1-T06 materials)
          if (h.root) h.root.visibility = t;
        }
      }
    }
  }

  private unload(id: string, lod: Lod): void {
    const h = this.cells.get(id);
    if (!h) return;
    const mesh = lod === 0 ? h.root : h.proxy;
    // P1-T06: materials/textures are deduped and shared world-wide
    // (cityMaterials.ts) — never dispose them with the cell.
    if (mesh) mesh.dispose(false, false);
    if (lod === 0) {
      h.root = null;
      h.rootReady = false;
    } else h.proxy = null;
    h.loadedAtS.delete(lod);
    this.lru.drop(`${id}:${String(lod)}`);
  }

  private unloadKey(key: string): void {
    const [id, lod] = key.split(':');
    if (id && lod) this.unload(id, Number(lod) as Lod);
  }

  dispose(): void {
    for (const [id] of [...this.cells]) {
      this.unload(id, 0);
      this.unload(id, 1);
    }
  }
}

function cellIdOf(h: CellHandle): string {
  return h.root?.name.match(/cell_(.+)_lod/)?.[1] ?? h.proxy?.name.match(/cell_(.+)_lod/)?.[1] ?? '';
}

export { Matrix, Vector3, Color3, Texture, ShaderMaterial };
